import {
  CatalogAccessType,
  getGetPlatformCatalogsQueryKey,
  PlatformCatalogAccessMatchingBlock,
  PlatformCatalogAccessMatchingRuleCriteria,
  PlatformCatalogDTO,
} from "@bvhi/backend-api/apis";
import {
  getGetPlatformCatalogByIdQueryKey,
  useGetPlatformCatalogById,
  useUpdatePlatformCatalog,
} from "@bvhi/backend-api/apis/api/platform-catalog/platform-catalog";
import { Box, Button, LoadingButton } from "@bvhi/ui-base";
import { AddIcon, UpdateIcon } from "@bvhi/ui-icons";
import { yupResolver } from "@hookform/resolvers/yup";
import { ArrowLeftIcon, ArrowRightIcon } from "@mui/x-date-pickers";
import { useQueryClient } from "@tanstack/react-query";
import { getRouteApi, useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import { FormProvider, SubmitHandler, useFieldArray, useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { AnyObjectSchema, array, object, string } from "yup";
import { BVHINavigationBackButton } from "../../../../../components/bvhi-navigation-back-button";
import { FormBlockerDialog } from "../../../../../components/form/form-blocker-dialog";
import { FormWithActionCard } from "../../../../../components/form/form-with-action-card";
import { NavigationBar } from "../../../../../components/navigation-bar";
import { useApiErrorContext } from "../../../../../context/use-api-error-context";
import { useAuthContext } from "../../../../../context/use-auth-context";
import { pick } from "../../../../../helpers/object";
import { useToast } from "../../../../../hooks/use-toast";
import { Route } from "../../../../../routes/_main/app/catalogs.create";
import { toNarrowedTypeOrNull } from "../../../../../utils/ts";
import { CatalogsCreateStepper } from "./catalogs-create-stepper";
import { CatalogsUpdateMatchingRulesAccessTypeCard } from "./catalogs-update-matching-rules-access-type-card";
import { CatalogsUpdateMatchingRulesBlocks } from "./catalogs-update-matching-rules-blocks";

export interface UpdateMatchingRulesFormValues {
  platformCatalogAccessType: CatalogAccessType;
  platformCatalogAccessMatchingBlocks: PlatformCatalogAccessMatchingBlock[];
}

interface Props {
  steps: string[];
  activeStep: number;
}

export const CatalogsUpdateMatchingRulesView = ({ steps, activeStep }: Props) => {
  const queryClient = useQueryClient();
  const { currentUser } = useAuthContext();
  const route = getRouteApi("/_main/app/catalogs/create");
  const { createdCatalogId } = route.useSearch();
  const { t } = useTranslation(["validation", "catalogs", "errors", "users", "common"]);
  const navigate = useNavigate({ from: Route.fullPath });
  const { setErrors } = useApiErrorContext();
  const { pushUpdateErrorToast, pushUpdateSuccessToast } = useToast();
  const { data: getPlatformCatalogByIdResponse, dataUpdatedAt: getPlatformDataByIdUpdatedAt } = useGetPlatformCatalogById(
    currentUser?.organisation?.id ?? "",
    createdCatalogId
  );
  const { mutate: updatePlatformCatalog, isPending: isUpdatePlatformCatalogPending } = useUpdatePlatformCatalog();

  const catalog = toNarrowedTypeOrNull<PlatformCatalogDTO>(getPlatformCatalogByIdResponse?.data, "id");

  const updateCatalogSchema: AnyObjectSchema = object({
    platformCatalogAccessType: string().oneOf(Object.values(CatalogAccessType)).required(t("validation:required")),
    platformCatalogAccessMatchingBlocks: array().when("platformCatalogAccessType", {
      is: (value: CatalogAccessType) => value === CatalogAccessType.PROTECTED,
      then: (schema) =>
        schema.of(
          object({
            id: string().optional(),
            platformCatalogAccessMatchingRules: array().of(
              object({
                column: string()
                  .when(["$requiredHeaders", "$optionalHeaders"], ([requiredHeaders, optionalHeaders], schema) =>
                    schema.oneOf([...(requiredHeaders as string[]), ...(optionalHeaders as string[])], t("validation:invalid"))
                  )
                  .required(t("validation:required")),
                criteria: string()
                  .when(["$requiredHeaders", "column"], (values: unknown[], schema) => {
                    const [requiredHeaders, column] = values as [string[], string];
                    return requiredHeaders.includes(column)
                      ? schema.oneOf(
                          [PlatformCatalogAccessMatchingRuleCriteria.EQUALS, PlatformCatalogAccessMatchingRuleCriteria.NOT_EQUALS],
                          t("validation:invalid.rule.criteria")
                        )
                      : schema;
                  })
                  .required(t("validation:required")),
                value: string()
                  .when("criteria", {
                    is: (value: string) => {
                      return (
                        [
                          PlatformCatalogAccessMatchingRuleCriteria.EQUALS.toString(),
                          PlatformCatalogAccessMatchingRuleCriteria.NOT_EQUALS.toString(),
                        ].indexOf(value) !== -1
                      );
                    },
                    then: (schema) => schema.min(1, t("validation:string.min.1")),
                    otherwise: (schema) => schema.matches(/^[+-]?(\d+(\.\d+)?|\.\d+)([eE][+-]?\d+)?$/, t("validation:only.numbers")),
                  })
                  .required(t("validation:required")),
              })
            ),
          })
        ),
      otherwise: (schema) => schema.optional(),
    }),
  });

  const {
    formState: { isDirty, isValid, isSubmitSuccessful, ...otherFormState },
    reset,
    control,
    handleSubmit,
    ...otherMethods
  } = useForm<UpdateMatchingRulesFormValues>({
    resolver: yupResolver(updateCatalogSchema),
    context: {
      requiredHeaders: catalog?.requiredHeaders ?? [],
      optionalHeaders: catalog?.customHeaders ?? [],
    },
    mode: "all",
    defaultValues: {
      platformCatalogAccessType: undefined,
      platformCatalogAccessMatchingBlocks: [{ platformCatalogAccessMatchingRules: [{ column: "", criteria: undefined, value: "" }] }],
    },
  });

  const { append, fields, remove } = useFieldArray({
    control: control,
    name: "platformCatalogAccessMatchingBlocks",
  });

  const platformCatalogAccessType: unknown = useWatch({ name: "platformCatalogAccessType", control: control });

  useEffect(() => {
    if (catalog) {
      reset({
        platformCatalogAccessMatchingBlocks: catalog.platformCatalogAccessMatchingBlocks,
        platformCatalogAccessType: catalog.platformCatalogAccessType,
      });
    }
  }, [catalog, getPlatformDataByIdUpdatedAt, reset]);

  const onSubmit: SubmitHandler<UpdateMatchingRulesFormValues> = (data) => {
    if (!catalog) return;

    const values = pick(catalog, ["id", "name", "notes", "platformCatalogStatus"]);

    updatePlatformCatalog(
      {
        supplierId: currentUser?.organisation?.id ?? "",
        platformCatalogId: createdCatalogId,
        data: {
          ...values,
          platformCatalogAccessType: data.platformCatalogAccessType,
          platformCatalogAccessMatchingBlocks: data.platformCatalogAccessMatchingBlocks,
        },
      },
      {
        onSuccess: () => {
          pushUpdateSuccessToast(t("catalogs:catalogs.ruleset.entity"));
        },
        onError: (error) => {
          if (error.status && [400, 403].includes(error.status)) {
            setErrors(error);
          }
          if (error.status === 500) pushUpdateErrorToast(t("catalogs:catalogs.ruleset.entity"));
        },
        onSettled: () => {
          void queryClient.invalidateQueries({ queryKey: getGetPlatformCatalogsQueryKey(currentUser?.organisation?.id ?? "") });
          void queryClient.invalidateQueries({
            queryKey: getGetPlatformCatalogByIdQueryKey(currentUser?.organisation?.id ?? "", createdCatalogId),
          });
        },
      }
    );
  };

  const actionCardContent = (
    <>
      {platformCatalogAccessType === CatalogAccessType.PROTECTED && (
        <Button
          startIcon={<AddIcon />}
          type="button"
          variant="contained"
          onClick={() =>
            append({
              platformCatalogAccessMatchingRules: [{ column: "", value: "", criteria: PlatformCatalogAccessMatchingRuleCriteria.EQUALS }],
            })
          }
        >
          {t("catalogs:catalogs.actions.add.ruleset")}
        </Button>
      )}

      <Box flex={1} />
      <Button
        disabled={createdCatalogId === ""}
        onClick={() => void navigate({ search: { createdCatalogId, step: activeStep - 1 }, replace: true })}
        startIcon={<ArrowLeftIcon />}
      >
        {t("common:button.prior.step")}
      </Button>

      {isDirty && (
        <LoadingButton
          disabled={!isDirty}
          loading={isUpdatePlatformCatalogPending}
          startIcon={<UpdateIcon />}
          type="submit"
          variant="contained"
        >
          {t("catalogs:catalogs.ruleset.button.save")}
        </LoadingButton>
      )}
      {!isDirty && (
        <Button
          onClick={() => void navigate({ to: "/app/catalogs/catalog/$catalogId", params: { catalogId: createdCatalogId }, replace: true })}
          startIcon={<ArrowRightIcon />}
        >
          {t("catalogs:catalogs.actions.submit.ruleset")}
        </Button>
      )}
    </>
  );

  return (
    <FormProvider
      control={control}
      formState={{ isDirty, isValid, isSubmitSuccessful, ...otherFormState }}
      handleSubmit={handleSubmit}
      reset={reset}
      {...otherMethods}
    >
      <form onSubmit={(event) => void handleSubmit(onSubmit)(event)}>
        <>
          <NavigationBar
            actions={[<BVHINavigationBackButton key={0} fallbackNavigateOptions={{ to: "/app/catalogs" }} />]}
            showBreadcrumbs={true}
          />
          <Box py={4}>
            <CatalogsCreateStepper activeStep={activeStep} steps={steps} />
          </Box>
          <FormWithActionCard actionCardContent={actionCardContent} contained>
            <CatalogsUpdateMatchingRulesAccessTypeCard />
            {platformCatalogAccessType === CatalogAccessType.PROTECTED && (
              <CatalogsUpdateMatchingRulesBlocks
                fields={fields}
                options={[...(catalog?.requiredHeaders ?? []), ...(catalog?.customHeaders ?? [])]}
                removeMatchingBlock={remove}
              />
            )}
          </FormWithActionCard>

          <FormBlockerDialog isDirty={isDirty && !isSubmitSuccessful} />
        </>
      </form>
    </FormProvider>
  );
};
